import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import {
  createUserType,
  getUserTypeByID,
  updateUserType,
} from "./services/userType.services";
import { clearUserTypeDetails } from "./userTypeSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { getAllActiveMenuSubMenuList } from "../menu/services/menu.services";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { getAllActiveRights } from "../rights/services/rights.services";
import { IRights } from "../rights/rightsModel";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";

// interface FormChangeWatcherProps {
//   formRenderProps: FormRenderProps;
// }

// const RightsChangeWatcher: React.FC<FormChangeWatcherProps> = ({
//   formRenderProps,
// }) => {
//   const MenuSubMenuList = useAppSelector((state) => state.menu.MenuSubMenuList);

//   MenuSubMenuList &&
//     MenuSubMenuList.length > 0 &&
//     MenuSubMenuList?.map((menu: any) => {
//       menu?.MenuSubList?.map((submenu: any) => {
//         const isMenuChecked = formRenderProps.valueGetter(`${submenu?.id}`);
//         useEffect(() => {
//           if (isMenuChecked === false) {
//             formRenderProps.onChange(`${submenu?.id}Rights`, {
//               value: null,
//             });
//           }
//         }, [isMenuChecked]);
//       });
//     });

//   return null;
// };

const CreateUserType: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user_type_guid = location.state?.user_type_guid;

  const loading = useAppSelector((state) => state.userType.loading);
  const UserTypeDetail = useAppSelector(
    (state) => state.userType.UserTypeDetail
  );
  const MenuSubMenuList = useAppSelector((state) => state.menu.MenuSubMenuList);
  const RightsList = useAppSelector((state) => state.rights.RightsList);

  useEffect(() => {
    if (user_type_guid) {
      const payload = {
        user_type_guid: user_type_guid,
      };
      dispatch(getUserTypeByID(payload));
    }
  }, [user_type_guid]);

  useEffect(() => {
    dispatch(getAllActiveMenuSubMenuList());
    dispatch(getAllActiveRights());

    return () => {
      dispatch(clearUserTypeDetails());
    };
  }, []);

  const handleMenuChange = (
    formRenderProps: FormRenderProps,
    menuName: string,
    isMenuChecked: boolean
  ) => {
    MenuSubMenuList &&
      MenuSubMenuList.length > 0 &&
      MenuSubMenuList?.forEach((menu: any) => {
        if (menu?.id === menuName) {
          menu?.MenuSubList?.forEach((submenu: any) => {
            formRenderProps.onChange(`${submenu?.id}`, {
              value: isMenuChecked,
            });
            if (isMenuChecked === false) {
              formRenderProps.onChange(`${submenu?.id}Rights`, {
                value: null,
              });
            }
          });
        }
      });
  };

  const handleSubMenuChange = (
    formRenderProps: FormRenderProps,
    subMenuName: string,
    isMenuChecked: boolean
  ) => {
    MenuSubMenuList &&
      MenuSubMenuList.length > 0 &&
      MenuSubMenuList?.map((menu: any) => {
        menu?.MenuSubList?.map((submenu: any) => {
          if (submenu?.id === subMenuName) {
            if (isMenuChecked === false) {
              formRenderProps.onChange(`${submenu?.id}Rights`, {
                value: null,
              });
            }
            if (submenu?.id && submenu?.UnderMenuID) {
              formRenderProps.onChange(`${submenu?.UnderMenuID}`, {
                value: true,
              });
            }
          }
        });
      });
  };

  const handleSubmit = async (values: any) => {
    interface ResultItem {
      menu_id: number;
      rights_id: string;
    }

    const result: ResultItem[] = Object.entries(values).reduce(
      (acc: ResultItem[], [key, value]) => {
        if (key.endsWith("Rights")) {
          const menuID: number = parseInt(key.replace("Rights", ""), 10);
          if (value) {
            const rightsID: string =
              Array.isArray(value) && value.length > 0 ? value.join(", ") : "";
            acc.push({ menu_id: menuID, rights_id: rightsID });
          }
        } else if (value === true) {
          const menuID: number = parseInt(key, 10);
          if (!values[`${menuID}Rights`]) {
            acc.push({ menu_id: menuID, rights_id: "" });
          }
        }
        return acc;
      },
      []
    );


    if (user_type_guid) {
      try {
        const updatePayload = {
          user_type_guid: user_type_guid,
          user_type: values.user_type ? values.user_type : "",
          isactive: values.isactive === 1,
          UserTypeRightsAssign: result || [],
        };
        const response = await dispatch(updateUserType(updatePayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    } else {
      try {
        const insertPayload = {
          user_type: values.user_type ? values.user_type : "",
          isactive: values.isactive === 1,
          UserTypeRightsAssign: result || [],
        };
        const response = await dispatch(createUserType(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      <Form
        onSubmit={handleSubmit}
        initialValues={UserTypeDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {user_type_guid ? "Update User Type" : "Create User Type"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="user_type"
                    maxLength="100"
                    label="User Type"
                    placeholder={"i.e. Admin"}
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="isactive"
                    label="Status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem></GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <ShadowCard style={{ padding: 12, marginTop: 10 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>Rights</Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  {MenuSubMenuList &&
                    MenuSubMenuList.length > 0 &&
                    MenuSubMenuList.map((menu: any) => {
                      return (
                        <>
                          <div
                            style={{
                              display: "flex",
                              width: "50%",
                              justifyContent: "space-between",
                              alignItems: "center",
                              minHeight: 45,
                            }}
                          >
                            <Field
                              key={menu?.id}
                              name={`${menu?.id}`}
                              label={menu?.menu_name}
                              component={FormCheckbox}
                              onChange={() =>
                                handleMenuChange(
                                  formRenderProps,
                                  menu?.id,
                                  formRenderProps.valueGetter(`${menu?.id}`)
                                )
                              }
                            />
                            {formRenderProps.valueGetter(`${menu?.id}`) ===
                              true && (
                              <Field
                                wrapperStyle={{ width: 300 }}
                                name={`${menu?.id}Rights`}
                                size="small"
                                isSelectAll={true}
                                component={FormMultiSelectionFiled}
                                options={RightsList?.map((right: IRights) => {
                                  return {
                                    value: right?.id,
                                    label: right?.name,
                                  };
                                })}
                              />
                            )}
                          </div>
                          {menu &&
                            menu?.MenuSubList &&
                            menu?.MenuSubList.length > 0 &&
                            menu?.MenuSubList?.map((submenu: any) => {
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    width: "60%",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    minHeight: 45,
                                  }}
                                >
                                  <Field
                                    wrapperStyle={{ marginLeft: "15%" }}
                                    key={submenu?.id}
                                    name={`${submenu?.id}`}
                                    label={submenu?.menu_name}
                                    component={FormCheckbox}
                                    onChange={() =>
                                      handleSubMenuChange(
                                        formRenderProps,
                                        submenu?.id,
                                        formRenderProps.valueGetter(
                                          `${submenu?.id}`
                                        )
                                      )
                                    }
                                  />
                                  {formRenderProps.valueGetter(
                                    `${submenu?.id}`
                                  ) === true && (
                                    <Field
                                      wrapperStyle={{ width: 300 }}
                                      // key={submenu?.id}
                                      name={`${submenu?.id}Rights`}
                                      size="small"
                                      isSelectAll={true}
                                      // label={`${submenu?.menu_name}Rights`}
                                      // validator={
                                      //   formRenderProps.valueGetter(
                                      //     `${submenu?.id}`
                                      //   ) && requiredArrayValidator
                                      // }
                                      component={FormMultiSelectionFiled}
                                      options={RightsList?.map(
                                        (right: IRights) => {
                                          return {
                                            value: right?.id,
                                            label: right?.name,
                                          };
                                        }
                                      )}
                                    />
                                  )}
                                </div>
                              );
                            })}
                        </>
                      );
                    })}
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={user_type_guid ? "Update" : "Create"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 4 }}
                    onClick={() => navigate("/usertype")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateUserType;
